import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { PasswordGenerator } from "@/components/PasswordGenerator";
import { ColorPicker } from "@/components/ColorPicker";
import { DatePicker } from "@/components/DatePicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Type } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import webToolsImage from "@/assets/web-tools-hero.jpg";

const StringGenerator = () => {
  const [length, setLength] = useState("16");
  const [result, setResult] = useState("");

  const generate = () => {
    const len = parseInt(length);
    if (isNaN(len) || len < 1 || len > 1000) {
      toast.error("Length must be between 1 and 1000");
      return;
    }

    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const array = new Uint32Array(len);
    crypto.getRandomValues(array);
    const str = Array.from(array)
      .map((x) => chars[x % chars.length])
      .join("");

    setResult(str);
    toast.success("String generated!");
  };

  const copy = () => {
    navigator.clipboard.writeText(result);
    toast.success("Copied to clipboard");
  };

  return (
    <Card className="p-6 shadow-[var(--shadow-medium)]">
      <div className="flex items-center gap-2 mb-6">
        <Type className="w-6 h-6 text-accent" />
        <h2 className="text-2xl font-bold">Random String</h2>
      </div>

      <div className="mb-4">
        <Label htmlFor="str-length">Length (1-1000)</Label>
        <Input
          id="str-length"
          type="number"
          value={length}
          onChange={(e) => setLength(e.target.value)}
          min="1"
          max="1000"
          className="mt-1"
        />
      </div>

      <Button onClick={generate} className="w-full mb-4">
        Generate String
      </Button>

      {result && (
        <div className="bg-secondary rounded-lg p-4">
          <div className="flex items-center justify-between">
            <code className="text-sm font-mono break-all flex-1">{result}</code>
            <Button size="sm" variant="ghost" onClick={copy}>
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

const WebTools = () => {
  return (
    <Layout>
      <SEO 
        title="Web Tools - Password Generator, Color Picker & More"
        description="Free web utility tools including password generator, random color picker, date picker, and string generator. Cryptographically secure and easy to use."
        canonical="https://numbers-generator.com/web-tools"
        keywords="password generator, color picker, random string, web tools, utility tools, secure password"
      />
      <div className="container mx-auto px-4 py-12">
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            RandomGen Web Tools
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Password generators, colors, dates, and utility tools
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto mb-12">
          <PasswordGenerator />
          <ColorPicker />
          <DatePicker />
          <StringGenerator />
        </div>

        {/* Web Tools Hero Image at Bottom */}
        <div className="rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
          <img 
            src={webToolsImage} 
            alt="Cybersecurity digital circuit board with glowing lock symbols - secure password generator, random string generator, and web development utility tools"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </Layout>
  );
};

export default WebTools;
